import { IconLiveChat, IconPhone } from "@btnet/blueprint-icons";
import { BTAvatar, btAvatarColorize } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { BdsButton } from "@buildertrend/components";

import { BTLoginTypes } from "types/enum";

import { routes } from "utilities/routes";
import { isNullOrWhitespace } from "utilities/string/string";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import {
    BTIconMailOutlined,
    BTIconMobileOutlined,
    BTIconUser,
} from "commonComponents/btWrappers/BTIcon";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { LinkButton } from "commonComponents/btWrappers/LinkButton/LinkButton";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { type UserProfileResponse } from "entity/login/Login/Login.api.types";

import "./BTUserInfoDisplay.less";

interface IBTUserInfoDisplayProps {
    globalUserId: number;
    displayName: string;
    userProfile?: UserProfileResponse;

    hideAllControls?: boolean;
    showManageUser: boolean;
    hideAvatar?: boolean;
}

export function BTUserInfoDisplay(props: IBTUserInfoDisplayProps) {
    const {
        globalUserId,
        displayName,
        userProfile,
        hideAllControls = false,
        showManageUser,
        hideAvatar = false,
    } = props;
    const phoneDisabled = isNullOrWhitespace(userProfile?.phone);
    const cellDisabled = isNullOrWhitespace(userProfile?.cellPhone);
    const emailDisabled = isNullOrWhitespace(userProfile?.email);
    const primaryContactDisabled = isNullOrWhitespace(userProfile?.primaryContact);
    const isSub = userProfile?.userType === BTLoginTypes.SUBS;
    const isBuilder = userProfile?.userType === BTLoginTypes.BUILDER;
    const showNewButton = isBuilder;
    const showOldButton = isSub;
    let manageUserLink = "";
    let manageUserPopoverText = "";
    switch (userProfile?.userType) {
        case BTLoginTypes.BUILDER:
            manageUserLink =
                routes.internalUsers.getListLink() +
                routes.internalUsers.getInternalUserDetailsLink(globalUserId);
            manageUserPopoverText = "Manage Internal User";
            break;
        case BTLoginTypes.SUBS:
            manageUserLink = routes.sub.getListLink() + routes.sub.getDetailsLink(globalUserId);
            manageUserPopoverText = "Manage Sub/Vendor";
            break;
    }

    return (
        <div className="BTUser-Popover" data-testid="BTUserInfoDisplay">
            <BTRow gutter={16} wrap={false}>
                {!hideAvatar && (
                    <BTCol>
                        <BTAvatar
                            size="lg"
                            colorize={btAvatarColorize[globalUserId % btAvatarColorize.length]}
                            text={displayName}
                            testid={`avatar-${globalUserId}`}
                        />
                    </BTCol>
                )}
                <BTCol className="text-overflow-auto">
                    <BTTitle level={2} style={{ margin: 0 }} className="text-overflow-auto">
                        {displayName}
                    </BTTitle>

                    {!emailDisabled && (
                        <BTRow>
                            <BTCol>
                                <BTLink
                                    data-testid="bt-user-send-email"
                                    linkType="email"
                                    type="tertiary"
                                    href={`mailto:${userProfile?.email}`}
                                    to=""
                                    isUnderline={false}
                                    className="BTUser-ContactInfo text-overflow-auto"
                                >
                                    <BTIconMailOutlined className="padding-right-xs BTUser-ContactInfoIcon" />
                                    {userProfile?.email}
                                </BTLink>
                            </BTCol>
                        </BTRow>
                    )}
                    {!primaryContactDisabled && (
                        <BTRow>
                            <BTCol>
                                <BTPopover content="Primary Contact">
                                    <BTIconUser className="padding-right-xs BTUser-ContactInfoIcon" />
                                    {userProfile?.primaryContact}
                                </BTPopover>
                            </BTCol>
                        </BTRow>
                    )}
                </BTCol>
            </BTRow>
            {!hideAllControls && (
                <div className="ControlWrapper">
                    <ShowOnPortal
                        builder
                        render={() => (
                            <>
                                {props.userProfile?.canStartChatConversation && displayName && (
                                    <BTLink
                                        useAutoSPARouting={true}
                                        shouldOpenNewTab
                                        to={routes.chat.getDraftChannelPathWithParticipantLink(
                                            globalUserId,
                                            displayName,
                                        )}
                                    >
                                        <BdsButton
                                            testid="chat-user"
                                            icon={<IconLiveChat />}
                                            ariaLabel="Direct message"
                                            displayType="tertiary"
                                            tooltip="Direct Message"
                                            disabled={!userProfile?.canStartChatConversation}
                                        />
                                    </BTLink>
                                )}
                            </>
                        )}
                    />
                    {/*
                When we are fully SPA for both Internal Users List and Sub/Vendor List, we can
                consolidate the output of the manage user button to the LinkButton component.
                We cannot do this now because the link button is expecting links for SPA.
                It appends /app to the beginning of the link.
                */}
                    {showManageUser && (
                        <ShowOnPortal
                            builder
                            render={() => (
                                <>
                                    {showNewButton && (
                                        <BTPopover
                                            content={manageUserPopoverText}
                                            data-testid="manageUserTooltip"
                                        >
                                            <LinkButton
                                                icon={<BTIconUser />}
                                                data-testid="manage-user"
                                                type="tertiary"
                                                to={manageUserLink}
                                                showInNewTab
                                            />
                                        </BTPopover>
                                    )}
                                    {showOldButton && (
                                        <BTPopover
                                            content={manageUserPopoverText}
                                            data-testid="manageUserTooltip"
                                        >
                                            <BTLink
                                                to={manageUserLink}
                                                shouldOpenNewTab
                                                useAutoSPARouting
                                            >
                                                <BTButtonDeprecated
                                                    icon={<BTIconUser />}
                                                    data-testid="manage-user"
                                                    type="tertiary"
                                                />
                                            </BTLink>
                                        </BTPopover>
                                    )}
                                </>
                            )}
                        />
                    )}

                    <BTLink
                        data-testid="bt-user-call-phone"
                        linkType="tel"
                        type="tertiary"
                        href={`tel:${userProfile?.phone}`}
                        to=""
                        isUnderline={false}
                        isDisabled={phoneDisabled}
                    >
                        <BdsButton
                            icon={<IconPhone />}
                            ariaLabel={!phoneDisabled ? `Call ${userProfile?.phone ?? ""}` : ""}
                            testid="phone"
                            displayType="tertiary"
                            tooltip={!phoneDisabled ? `Call ${userProfile?.phone ?? ""}` : ""}
                            disabled={phoneDisabled}
                        />
                    </BTLink>

                    {!cellDisabled && (
                        <BTPopover
                            content={`Call cell phone ${userProfile?.cellPhone ?? ""}`}
                            data-testid="manageUserTooltip"
                            disabled={cellDisabled}
                        >
                            <BTLink
                                data-testid="bt-user-call-cell"
                                linkType="tel"
                                type="tertiary"
                                href={`tel:${userProfile?.cellPhone}`}
                                to=""
                                isUnderline={false}
                                isDisabled={cellDisabled}
                            >
                                <BTButtonDeprecated
                                    icon={<BTIconMobileOutlined />}
                                    data-testid="phone"
                                    type="tertiary"
                                    disabled={cellDisabled}
                                />
                            </BTLink>
                        </BTPopover>
                    )}
                </div>
            )}
        </div>
    );
}
